import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import confetti from "canvas-confetti";
import { useAuth } from "../../context/AuthContext";
import api, { API_BASE } from "../../utils/api";
import { Badge } from "../../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  FileText,
  ShieldCheck,
  Award,
  CalendarDays,
  User,
  Users,
  Briefcase,
  ChevronRight,
  ChevronDown,
  Loader2,
  ClipboardList,
  CheckCircle2,
  X,
  Upload,
  AlertCircle,
  BookOpen,
  GraduationCap,
  Clock,
  PlayCircle,
  FileCheck,
  MessageSquare,
  Phone,
  Mail,
  MapPin,
  Headphones,
  HeartHandshake,
  Info,
  Eye,
  Download,
  FileSignature,
} from "lucide-react";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";
import { generateContractPdf } from "../../utils/contract-pdf";
import { courseContentTypeLabel, durationLabel } from "../../utils/learning";
import {
  SignedContract,
  SignedContractDocument,
  formatContractDate,
} from "../../components/onboarding/SignedContractDocument";

interface TemplateItem {
  id: string | number;
  name?: string;
  title?: string;
  required?: boolean;
  description?: string;
  status?: string;
  progress?: number;
}

interface SupportNetworkItem {
  id: string;
  label: string;
  userId: number | null;
  user?: {
    id: number;
    name: string;
    email: string;
    mobile: string | null;
  } | null;
}

interface PortalProcedureStep {
  id: number | string;
  number: number;
  title: string | null;
  content: string | null;
}

interface TrainingProcedure {
  id: number;
  name: string;
  description: string | null;
  whyImportant: string | null;
  howToComplete: string | null;
  cadence: string;
  videoEmbed: string | null;
  version: number;
  steps?: PortalProcedureStep[];
}

interface TrainingPlanData {
  id: number;
  name: string;
  description: string;
  status: string;
  procedures: TrainingProcedure[];
}

/**
 * A pre-start course on the onboarding template. `thumbnailUrl` arrives already
 * signed for display; `status` is the hire's own record for the course.
 */
interface PortalLearningItem {
  id: number;
  title: string;
  description: string | null;
  thumbnailUrl: string | null;
  contentType: "SCORM" | "PDF" | "PPTX";
  estimatedDurationMinutes: number | null;
  issuesCertificate: boolean;
  status: "Assigned" | "In Progress" | "Completed";
  startedAt: string | null;
  completedAt: string | null;
  scorePercent: number | null;
  scormBookmark: boolean;
}

interface TemplateData {
  forms: TemplateItem[];
  policies: TemplateItem[];
  licences: TemplateItem[];
  learning?: PortalLearningItem[];
  supportNetwork?: SupportNetworkItem[];
}

interface CardProgress {
  forms: { progress: number; status: string };
  policies: { progress: number; status: string };
  licences: { progress: number; status: string };
  learning?: { completed: number; total: number; progress: number; status: string };
}

interface AcknowledgedPolicy {
  id: string | number;
  title?: string;
  acknowledgedAt: string;
}

interface LicenceUpload {
  id: string | number;
  qualificationId?: string;
  attachmentId?: string | null;
  fileName?: string | null;
  issueDate?: string | null;
  expiryDate?: string | null;
  uploadedAt: string;
}

interface PolicyContent {
  id: string | number;
  title: string;
  description?: string;
  content?: string;
  fileUrl?: string | null;
}

interface OnboardingPortalData {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  startDate: string | null;
  progress: number;
  status: string;
  formsStatus: string;
  policiesStatus: string;
  trainingStatus: string;
  jobPosition: {
    jobRole: {
      roleName: string;
    } | null;
  } | null;
  manager: {
    id: number;
    name: string;
  } | null;
  buddy: {
    id: number;
    name: string;
  } | null;
  cardProgress?: CardProgress;
  templateData?: TemplateData;
  trainingPlanData?: TrainingPlanData;
  acknowledgedPolicies?: AcknowledgedPolicy[];
  licenceUploads?: LicenceUpload[];
  contract?: SignedContract | null;
}

interface SectionProgress {
  id: string;
  title: string;
  description: string;
  progress: number;
  icon: React.ReactNode;
  iconBg: string;
  iconColor: string;
  borderColor: string;
}

const TAB_ITEMS = [
  { id: "dashboard", label: "Dashboard" },
  { id: "forms", label: "Pre-Start Forms" },
  { id: "policies", label: "Policies" },
  { id: "licences", label: "Licenses & Qualifications" },
  { id: "learning", label: "Learning" },
  { id: "support", label: "Support Network" },
  { id: "training", label: "Training Plan" },
];

interface ChecklistItem {
  id: string;
  name: string;
  required: boolean;
  completed: boolean;
}

interface ChecklistSection {
  id: string;
  title: string;
  icon: React.ReactNode;
  iconBg: string;
  iconColor: string;
  completedCount: number;
  totalCount: number;
  progress: number;
  items: ChecklistItem[];
}

interface LicenceItem {
  id: string;
  name: string;
  mandatory: boolean;
  uploaded: boolean;
  fileName: string | null;
  issueDate: string | null;
  expiryDate: string | null;
  attachmentId?: string | null;
}

// Overall completion shown in the welcome banner. The entity's stored
// `progress` counter is not reliable (it only ever reaches 75), so derive
// the total from the section cards instead. Sections with no assigned
// template items are excluded so they can't drag the total down.
const computeOverallProgress = (portal: OnboardingPortalData): number => {
  const td = portal.templateData;
  const cp = portal.cardProgress;
  const sections = [
    { total: td?.forms?.length ?? 0, progress: cp?.forms?.progress ?? 0 },
    { total: td?.policies?.length ?? 0, progress: cp?.policies?.progress ?? 0 },
    { total: td?.licences?.length ?? 0, progress: cp?.licences?.progress ?? 0 },
    { total: td?.learning?.length ?? 0, progress: cp?.learning?.progress ?? 0 },
  ].filter((s) => s.total > 0);
  if (sections.length === 0) return 0;
  return Math.round(
    sections.reduce((sum, s) => sum + s.progress, 0) / sections.length
  );
};

const MyOnboardingPortal: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [searchParams] = useSearchParams();

  // HR preview mode: /my-onboarding?requestId=<id> renders the candidate
  // portal for that onboarding request (read-only) instead of the caller's
  // own portal. Data comes from the CASL-gated GET /api/onboarding/:id/portal.
  const previewRequestId = searchParams.get("requestId");
  const isPreviewMode = !!previewRequestId;

  // Deep-link support: /my-onboarding?tab=forms opens straight on a tab
  // (e.g. form pages return the hire to Pre-Start Forms after submitting).
  const initialTab = searchParams.get("tab");
  const [activeTab, setActiveTab] = useState(
    initialTab && TAB_ITEMS.some((t) => t.id === initialTab) ? initialTab : "dashboard"
  );
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [onboardingData, setOnboardingData] = useState<OnboardingPortalData | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [licences, setLicences] = useState<LicenceItem[]>([]);
  const [acknowledgedPolicies, setAcknowledgedPolicies] = useState<Set<string>>(new Set());
  const [selectedProcedure, setSelectedProcedure] = useState<TrainingProcedure | null>(null);
  const [showProcedureModal, setShowProcedureModal] = useState(false);
  const [selectedPolicy, setSelectedPolicy] = useState<PolicyContent | null>(null);
  const [showPolicyModal, setShowPolicyModal] = useState(false);
  const [policyActionLoading, setPolicyActionLoading] = useState(false);
  const [uploadTargetLicence, setUploadTargetLicence] = useState<string | null>(null);
  const [licenceActionLoading, setLicenceActionLoading] = useState(false);
  const licenceFileInputRef = React.useRef<HTMLInputElement>(null);
  const [viewingContract, setViewingContract] = useState(false);
  const [downloadingContract, setDownloadingContract] = useState(false);
  const contractRef = React.useRef<HTMLDivElement>(null);
  const [showCelebration, setShowCelebration] = useState(false);

  const celebrationKey = onboardingData ? `onboarding-celebrated-${onboardingData.id}` : null;

  // Empty real state for users with no onboarding request (e.g., existing
  // users created outside the hiring flow): their own identity, zeroed
  // progress, and no template/training content.
  const emptyData: OnboardingPortalData = {
    id: 0,
    firstName: currentUser?.name?.split(" ")[0] || "there",
    lastName: currentUser?.name?.split(" ").slice(1).join(" ") || "",
    email: currentUser?.email || "",
    startDate: null,
    progress: 0,
    status: "Not Started",
    formsStatus: "Not Started",
    policiesStatus: "Not Started",
    trainingStatus: "Not Started",
    jobPosition: null,
    manager: null,
    buddy: null,
    cardProgress: {
      forms: { progress: 0, status: "Not Started" },
      policies: { progress: 0, status: "Not Started" },
      licences: { progress: 0, status: "Not Started" },
      learning: { completed: 0, total: 0, progress: 0, status: "Not Started" },
    },
  };

  // Compute section progress from real API data
  const getSectionProgress = (): SectionProgress[] => {
    const cp = onboardingData?.cardProgress;
    const sections: SectionProgress[] = [
      {
        id: "forms",
        title: "Pre-Start Forms",
        description: "Complete required compliance forms",
        progress: cp?.forms?.progress ?? 0,
        icon: <FileText className="w-5 h-5" />,
        iconBg: "bg-purple-50",
        iconColor: "text-purple-500",
        borderColor: "border-purple-100",
      },
      {
        id: "policies",
        title: "Policies & Documents",
        description: "Required reading and acknowledgements",
        progress: cp?.policies?.progress ?? 0,
        icon: <ShieldCheck className="w-5 h-5" />,
        iconBg: "bg-red-50",
        iconColor: "text-red-500",
        borderColor: "border-red-100",
      },
      {
        id: "licences",
        title: "Licences & Qualifications",
        description: "Upload your mandatory licences and certificates",
        progress: cp?.licences?.progress ?? 0,
        icon: <Award className="w-5 h-5" />,
        iconBg: "bg-teal-50",
        iconColor: "text-teal-500",
        borderColor: "border-teal-100",
      },
    ];
    // Learning only exists as a section when the template actually carries courses.
    if ((onboardingData?.templateData?.learning?.length ?? 0) > 0) {
      sections.push({
        id: "learning",
        title: "Learning",
        description: "Complete your pre-start training",
        progress: cp?.learning?.progress ?? 0,
        icon: <GraduationCap className="w-5 h-5" />,
        iconBg: "bg-indigo-50",
        iconColor: "text-indigo-500",
        borderColor: "border-indigo-100",
      });
    }
    return sections;
  };

  // Build checklist sections from real template data + status
  const getChecklistSections = (): ChecklistSection[] => {
    const td = data.templateData;
    const cp = data.cardProgress;
    if (!td) return [];

    const sections: ChecklistSection[] = [];

    // Forms section
    if (td.forms && td.forms.length > 0) {
      const items = td.forms.map((f) => {
        const name = f.name || f.title || "";
        return {
          id: String(f.id),
          name,
          required: f.required ?? true,
          completed: f.status === "Completed",
        };
      });
      const requiredItems = items.filter((i) => i.required);
      sections.push({
        id: "forms",
        title: "Pre-Start Forms",
        icon: <FileText className="w-5 h-5" />,
        iconBg: "bg-purple-50",
        iconColor: "text-purple-500",
        completedCount: requiredItems.filter((i) => i.completed).length,
        totalCount: requiredItems.length,
        progress: cp?.forms?.progress ?? 0,
        items,
      });
    }

    // Policies section
    if (td.policies && td.policies.length > 0) {
      const items = td.policies.map((p) => {
        const name = p.title || p.name || "";
        return {
          id: String(p.id),
          name,
          required: p.required ?? true,
          completed: acknowledgedPolicies.has(String(p.id)),
        };
      });
      sections.push({
        id: "policies",
        title: "Policies & Documents",
        icon: <ShieldCheck className="w-5 h-5" />,
        iconBg: "bg-red-50",
        iconColor: "text-red-500",
        completedCount: items.filter((i) => i.completed).length,
        totalCount: td.policies.length,
        progress: cp?.policies?.progress ?? 0,
        items,
      });
    }

    // Licences section
    if (td.licences && td.licences.length > 0) {
      const items = td.licences.map((l) => {
        const name = l.name || l.title || "";
        const licence = licences.find((ld) => ld.id === String(l.id));
        return {
          id: String(l.id),
          name,
          required: l.required ?? true,
          completed: licence ? licence.uploaded : false,
        };
      });
      const requiredItems = items.filter((i) => i.required);
      sections.push({
        id: "licences",
        title: "Licences & Qualifications",
        icon: <Award className="w-5 h-5" />,
        iconBg: "bg-teal-50",
        iconColor: "text-teal-500",
        completedCount: requiredItems.filter((i) => i.completed).length,
        totalCount: requiredItems.length,
        progress: cp?.licences?.progress ?? 0,
        items,
      });
    }

    // Learning section — pre-start courses from the template
    if (td.learning && td.learning.length > 0) {
      const items = td.learning.map((course) => ({
        id: String(course.id),
        name: course.title,
        required: true,
        completed: course.status === "Completed",
      }));
      sections.push({
        id: "learning",
        title: "Learning",
        icon: <GraduationCap className="w-5 h-5" />,
        iconBg: "bg-indigo-50",
        iconColor: "text-indigo-500",
        completedCount: items.filter((i) => i.completed).length,
        totalCount: td.learning.length,
        progress: cp?.learning?.progress ?? 0,
        items,
      });
    }

    return sections;
  };

  // Get description for support network role
  const getSupportRoleDescription = (roleId: string): string => {
    const descriptions: Record<string, string> = {
      "hiring-manager": "Day-to-day oversight, performance goals, project assignments",
      "onboarding-buddy": "Friendly guide for your first weeks, culture ambassador",
      "hr-manager": "HR policies, contracts, payroll queries, onboarding support",
      "whs-manager": "Safety inductions, incident reporting, WHS compliance",
      "it-support": "System access, equipment issues, software troubleshooting",
    };
    return descriptions[roleId] || "Your support contact during onboarding";
  };

  // Fetch and display a policy's full content
  const handleViewPolicy = async (policyId: string | number) => {
    setPolicyActionLoading(true);
    try {
      const res = await api.get<{ success: boolean; data: PolicyContent }>(
        `/api/onboarding/my-portal/policies/${policyId}`
      );
      if (res.success && res.data) {
        setSelectedPolicy(res.data);
        setShowPolicyModal(true);
      }
    } catch (error) {
      // The API wrapper already toasts the server's error message
      console.error("Error loading policy:", error);
    } finally {
      setPolicyActionLoading(false);
    }
  };

  // Download the signed contract as a PDF of the rendered document. The
  // document mounts off-screen while the capture runs (same html2pdf
  // pipeline as the recruitment Offer view), then unmounts — no UI flash.
  const handleDownloadContract = () => {
    if (!onboardingData?.contract) return;
    setDownloadingContract(true);
  };

  useEffect(() => {
    const contract = onboardingData?.contract;
    if (!downloadingContract || !contract || !contractRef.current) return;
    const node = contractRef.current;
    const safeName = (contract.candidateName || "Employment")
      .replace(/[^a-z0-9\- ]/gi, "")
      .trim();
    generateContractPdf(node, `Contract-${safeName || "Employment"}.pdf`)
      .catch((error) => {
        console.error("Failed to generate contract PDF:", error);
        toast.error("Failed to download contract");
      })
      .finally(() => setDownloadingContract(false));
  }, [downloadingContract, onboardingData]);

  // Persist a policy acknowledgment to the backend
  const handleAcknowledgePolicy = async (policy: TemplateItem) => {
    setPolicyActionLoading(true);
    try {
      const res = await api.post<{
        success: boolean;
        data: {
          acknowledgedPolicies: AcknowledgedPolicy[];
          policiesStatus: string;
          policiesCardProgress: { progress: number; status: string };
          progress: number;
        };
      }>(`/api/onboarding/my-portal/policies/${policy.id}/acknowledge`, {});

      if (res.success && res.data) {
        setAcknowledgedPolicies(new Set(res.data.acknowledgedPolicies.map((p) => String(p.id))));
        // Patch portal data so the dashboard reflects the change immediately
        setOnboardingData((prev) =>
          prev
            ? {
                ...prev,
                acknowledgedPolicies: res.data.acknowledgedPolicies,
                policiesStatus: res.data.policiesStatus,
                progress: res.data.progress,
                cardProgress: {
                  forms: prev.cardProgress?.forms ?? { progress: 0, status: "Not Started" },
                  policies: res.data.policiesCardProgress,
                  licences: prev.cardProgress?.licences ?? { progress: 0, status: "Not Started" },
                  learning: prev.cardProgress?.learning ?? { completed: 0, total: 0, progress: 0, status: "Not Started" },
                },
              }
            : prev
        );
        toast.success(`Acknowledged: ${policy.title || policy.name}`);
      }
    } catch (error) {
      // The API wrapper already toasts the server's error message
      console.error("Error acknowledging policy:", error);
    } finally {
      setPolicyActionLoading(false);
    }
  };

  // Register an uploaded file (or date update) for a template licence
  const saveLicenceUpload = async (licenceId: string, payload: Record<string, any>) => {
    const res = await api.post<{
      success: boolean;
      data: {
        licenceUploads: LicenceUpload[];
        licencesCardProgress: { progress: number; status: string };
        progress: number;
      };
    }>(`/api/onboarding/my-portal/licences/${licenceId}/upload`, payload);

    if (res.success && res.data) {
      const uploads = res.data.licenceUploads ?? [];
      setLicences((prev) =>
        prev.map((l) => {
          if (l.id !== licenceId) return l;
          const upload = uploads.find((u) => String(u.id) === String(licenceId));
          return {
            ...l,
            uploaded: !!upload,
            fileName: upload?.fileName ?? null,
            issueDate: upload?.issueDate ?? null,
            expiryDate: upload?.expiryDate ?? null,
            attachmentId: upload?.attachmentId ?? null,
          };
        })
      );
      // Patch portal data so the dashboard reflects the change immediately
      setOnboardingData((prev) =>
        prev
          ? {
              ...prev,
              licenceUploads: uploads,
              progress: res.data.progress,
              cardProgress: {
                forms: prev.cardProgress?.forms ?? { progress: 0, status: "Not Started" },
                policies: prev.cardProgress?.policies ?? { progress: 0, status: "Not Started" },
                licences: res.data.licencesCardProgress,
                learning: prev.cardProgress?.learning ?? { completed: 0, total: 0, progress: 0, status: "Not Started" },
              },
            }
          : prev
      );
    }
  };

  // Open the file picker for a licence
  const handleUploadClick = (licenceId: string) => {
    setUploadTargetLicence(licenceId);
    licenceFileInputRef.current?.click();
  };

  // Upload the selected file to S3, then register it against the licence
  const handleLicenceFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file
    if (!file || !uploadTargetLicence) return;

    setLicenceActionLoading(true);
    try {
      const uploadFormData = new FormData();
      uploadFormData.append("image", file);
      uploadFormData.append("context", "qualifications");
      const uploadResult = await api.post("/api/upload/s3", uploadFormData);

      if (!uploadResult?.url) throw new Error("File upload failed");

      await saveLicenceUpload(uploadTargetLicence, {
        fileName: file.name,
        url: uploadResult.url,
        key: uploadResult.key || uploadResult.filename,
        size: uploadResult.size,
        mimetype: uploadResult.mimetype,
      });
      toast.success(`Uploaded: ${file.name}`);
    } catch (error) {
      // The API wrapper already toasts the server's error message
      console.error("Error uploading licence:", error);
    } finally {
      setLicenceActionLoading(false);
      setUploadTargetLicence(null);
    }
  };

  // Persist issue/expiry date changes for an uploaded licence
  const handleLicenceDateChange = async (
    licenceId: string,
    field: "issueDate" | "expiryDate",
    value: string
  ) => {
    setLicences((prev) =>
      prev.map((l) => (l.id === licenceId ? { ...l, [field]: value } : l))
    );
    try {
      await saveLicenceUpload(licenceId, { [field]: value || null });
    } catch (error) {
      console.error("Error saving licence dates:", error);
    }
  };

  useEffect(() => {
    const fetchOnboarding = async () => {
      try {
        const res = await api.get<{ success: boolean; data: OnboardingPortalData; message?: string }>(
          isPreviewMode
            ? `/api/onboarding/${previewRequestId}/portal`
            : "/api/onboarding/my-portal",
          { skipErrorToast: true }
        );

        if (res.success && res.data) {
          setOnboardingData(res.data);
        } else if (isPreviewMode) {
          setPreviewError(res.message || "Failed to load the candidate's onboarding portal");
        } else {
          // No onboarding request for this user — render the empty state
          setOnboardingData(null);
        }
      } catch (error: any) {
        console.error("Error fetching onboarding data:", error);
        if (isPreviewMode) {
          setPreviewError(error?.message || "Failed to load the candidate's onboarding portal");
        } else {
          setOnboardingData(null);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchOnboarding();
  }, [isPreviewMode, previewRequestId]);

  // Sync template-driven state when onboarding data loads
  useEffect(() => {
    const templateLicences = onboardingData?.templateData?.licences ?? [];
    const uploads = onboardingData?.licenceUploads ?? [];
    setLicences(
      templateLicences.map((l) => {
        const upload = uploads.find((u) => String(u.id) === String(l.id));
        return {
          id: String(l.id),
          name: l.name || l.title || "",
          mandatory: l.required ?? true,
          uploaded: !!upload,
          fileName: upload?.fileName ?? null,
          issueDate: upload?.issueDate ?? null,
          expiryDate: upload?.expiryDate ?? null,
          attachmentId: upload?.attachmentId ?? null,
        };
      })
    );
    setAcknowledgedPolicies(
      new Set((onboardingData?.acknowledgedPolicies ?? []).map((p) => String(p.id)))
    );
  }, [onboardingData]);

  // Trigger the celebration once when onboarding reaches 100% (never in HR
  // preview mode — the confetti moment belongs to the candidate)
  useEffect(() => {
    if (!onboardingData || !celebrationKey || isPreviewMode) return;
    if (
      computeOverallProgress(onboardingData) === 100 &&
      !localStorage.getItem(celebrationKey)
    ) {
      setShowCelebration(true);
    }
  }, [onboardingData, celebrationKey, isPreviewMode]);

  // Fire confetti side-cannons while the celebration overlay is visible
  useEffect(() => {
    if (!showCelebration) return;
    const duration = 5 * 1000;
    const animationEnd = Date.now() + duration;
    const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 100 };
    const randomInRange = (min: number, max: number) =>
      Math.random() * (max - min) + min;

    const interval = setInterval(() => {
      const timeLeft = animationEnd - Date.now();
      if (timeLeft <= 0) {
        clearInterval(interval);
        return;
      }
      const particleCount = 50 * (timeLeft / duration);
      confetti({ ...defaults, particleCount, origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 } });
      confetti({ ...defaults, particleCount, origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 } });
    }, 250);

    return () => clearInterval(interval);
  }, [showCelebration]);

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "Not set";
    const date = new Date(dateString + "T00:00:00");
    return date.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const getDaysToStart = () => {
    if (!onboardingData?.startDate) return null;
    const start = new Date(onboardingData.startDate + "T00:00:00");
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diff = Math.ceil((start.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    return diff;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-gray-900" />
      </div>
    );
  }

  // HR preview failed (no permission, or the request doesn't exist) — don't
  // fall back to the caller's own empty state, that would show the admin's
  // identity instead of the hire's.
  if (isPreviewMode && !onboardingData) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-gray-500">
        <p>{previewError || "Onboarding portal not available"}</p>
        <button
          onClick={() => window.close()}
          className="mt-4 px-4 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
        >
          Close Tab
        </button>
      </div>
    );
  }

  const data = onboardingData || emptyData;
  const daysToStart = getDaysToStart();
  const overallProgress = onboardingData ? computeOverallProgress(onboardingData) : 0;
  const isOnboardingComplete = overallProgress === 100;

  // Progress per dashboard section, keyed by tab/section id
  const sectionProgressById: Record<string, number> = {
    forms: data.cardProgress?.forms?.progress ?? 0,
    policies: data.cardProgress?.policies?.progress ?? 0,
    licences: data.cardProgress?.licences?.progress ?? 0,
    learning: data.cardProgress?.learning?.progress ?? 0,
  };

  // Learning only appears when the template carries courses — the tab is absent, not empty.
  const learningItems = data.templateData?.learning ?? [];
  const visibleTabs = TAB_ITEMS.filter(
    (tab) => tab.id !== "learning" || learningItems.length > 0
  );

  // Calculate licence stats. A licence counts as submitted once it has both
  // a file and an issue date (expiry stays optional).
  const isLicenceComplete = (l: LicenceItem) => l.uploaded && !!l.issueDate;
  const uploadedCount = licences.filter(isLicenceComplete).length;
  const mandatoryMissingCount = licences.filter((l) => l.mandatory && !isLicenceComplete(l)).length;
  const optionalCount = licences.filter((l) => !l.mandatory).length;

  // Learning stats for the pre-start training tab.
  const learningCompletedCount = learningItems.filter((c) => c.status === "Completed").length;
  const learningInProgressCount = learningItems.filter((c) => c.status === "In Progress").length;
  const learningNotStartedCount = learningItems.filter((c) => c.status === "Assigned").length;

  return (
    <div className="flex flex-col space-y-6 animate-in fade-in duration-500 pb-10">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-gray-500">
        <span>Onboarding</span>
        <span>/</span>
        <span className="text-gray-900 font-medium">
          {isPreviewMode ? `${data.firstName} ${data.lastName}`.trim() : "My Portal"}
        </span>
      </div>

      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
          {isPreviewMode ? `${data.firstName}'s Onboarding Portal` : "My Onboarding Portal"}
        </h1>
        <p className="text-gray-500 text-sm font-medium">
          {isPreviewMode
            ? "The onboarding journey exactly as the new hire sees it"
            : "Welcome to your personalised onboarding journey"}
        </p>
      </div>

      {/* HR preview notice */}
      {isPreviewMode && (
        <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 text-blue-800 rounded-xl px-4 py-3 text-sm">
          <Eye className="w-4 h-4 shrink-0" />
          <span>
            You are previewing <span className="font-semibold">{data.firstName} {data.lastName}</span>&apos;s
            onboarding portal. This is a read-only view — actions are disabled.
          </span>
        </div>
      )}

      {/* Tab Navigation — wraps instead of clipping so every tab (including
          the last, Training Plan) stays visible on narrow screens */}
      <div className="flex items-center gap-1 bg-gray-100 p-1 rounded-xl flex-wrap">
        {visibleTabs.map((tab) => {
          const sectionProgress = sectionProgressById[tab.id];
          const isComplete = sectionProgress === 100;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                isComplete
                  ? activeTab === tab.id
                    ? "bg-green-600 text-white shadow-sm"
                    : "bg-green-100 text-green-700 hover:bg-green-200"
                  : activeTab === tab.id
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      {activeTab === "dashboard" && (
        <div className="space-y-6">
          {/* Welcome Banner */}
          <div className="bg-slate-900 rounded-2xl p-8 text-white relative overflow-hidden">
            <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
              <div className="space-y-4">
                <h2 className="text-3xl font-bold">
                  Welcome {data.firstName} 👋
                </h2>
                <p className="text-slate-300">
                  Let's get your profile setup and onboarding journey completed,
                  so you're ready for your first day
                </p>
                {!isPreviewMode && (
                  <button
                    onClick={() => setActiveTab("forms")}
                    disabled={isOnboardingComplete}
                    className={`inline-flex items-center gap-2 text-sm font-semibold px-5 py-2.5 rounded-xl transition-colors ${
                      isOnboardingComplete
                        ? "bg-slate-700 text-slate-400 cursor-not-allowed"
                        : "bg-green-600 hover:bg-green-500 text-white"
                    }`}
                  >
                    Get Started
                    <ChevronRight className="w-4 h-4" />
                  </button>
                )}
                <div className="flex items-center gap-4 pt-2">
                  <div className="bg-slate-800 rounded-xl px-5 py-3 text-center">
                    <p className="text-2xl font-bold">{daysToStart ?? "—"}</p>
                    <p className="text-xs text-slate-400">Days to Start</p>
                  </div>
                  <div className="bg-slate-800 rounded-xl px-5 py-3 text-center">
                    <p className="text-2xl font-bold">{overallProgress}%</p>
                    <p className="text-xs text-slate-400">Complete</p>
                  </div>
                </div>
              </div>

              {/* Welcome Message */}
              <div className="bg-white text-gray-700 rounded-2xl p-6 w-full max-w-xl shadow-xl border-l-4 border-blue-500 self-center">
                <p className="text-sm font-medium text-gray-900">
                  Hi {data.firstName},
                </p>
                <p className="text-xs leading-relaxed mt-3">
                  A very warm welcome to the Proview team! We are thrilled to have
                  you join us. Every single person here plays a critical role in
                  our success. Your contributions during your time with us will
                  help to build a world-class, high-performing team.
                </p>
                <p className="text-xs leading-relaxed mt-3">
                  As you settle in, there are three core priorities that guide
                  everything we do:
                </p>
                <ul className="mt-2 space-y-2">
                  <li className="text-xs leading-relaxed">
                    <span className="font-semibold text-gray-900">Safety First:</span>{" "}
                    The health and well-being of our people always comes first. We
                    do not compromise on safety. We look out for each other and
                    ensure everyone goes home safely every single day.
                  </li>
                  <li className="text-xs leading-relaxed">
                    <span className="font-semibold text-gray-900">High Performance:</span>{" "}
                    We set a high bar, take pride in our work and execution, and
                    deliver excellence together.
                  </li>
                  <li className="text-xs leading-relaxed">
                    <span className="font-semibold text-gray-900">Continuous Growth:</span>{" "}
                    We are committed to your development. We contribute
                    collectively to the satisfaction of our customers and growth
                    of the business.
                  </li>
                </ul>
                <p className="text-xs leading-relaxed mt-3">
                  Enjoy your onboarding, get to know the team, and welcome aboard!
                </p>
                <p className="text-xs mt-3">
                  Best regards,
                  <br />
                  <span className="font-semibold text-gray-900">Moe Hannaway</span>
                  <span className="text-gray-500"> - National HR Manager</span>
                </p>
                <button
                  onClick={() => window.open("/proview_scaffolding_our_values.pdf", "_blank")}
                  className="mt-4 inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white text-xs font-semibold px-4 py-2 rounded-xl transition-colors"
                >
                  View Company Values
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </div>

          {/* Info Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white p-5 rounded-2xl border border-gray-100 flex items-center gap-4">
              <div className="p-3 bg-blue-50 rounded-xl">
                <CalendarDays className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <p className="text-xs text-gray-500 font-medium">Start Date</p>
                <p className="text-sm font-semibold text-gray-900">{formatDate(data.startDate)}</p>
              </div>
            </div>
            <div className="bg-white p-5 rounded-2xl border border-gray-100 flex items-center gap-4">
              <div className="p-3 bg-purple-50 rounded-xl">
                <User className="w-5 h-5 text-purple-500" />
              </div>
              <div>
                <p className="text-xs text-gray-500 font-medium">Manager</p>
                <p className="text-sm font-semibold text-gray-900">{data.manager?.name || "—"}</p>
              </div>
            </div>
            <div className="bg-white p-5 rounded-2xl border border-gray-100 flex items-center gap-4">
              <div className="p-3 bg-teal-50 rounded-xl">
                <Users className="w-5 h-5 text-teal-500" />
              </div>
              <div>
                <p className="text-xs text-gray-500 font-medium">Buddy</p>
                <p className="text-sm font-semibold text-gray-900">{data.buddy?.name || "—"}</p>
              </div>
            </div>
            <div className="bg-white p-5 rounded-2xl border border-gray-100 flex items-center gap-4">
              <div className="p-3 bg-amber-50 rounded-xl">
                <Briefcase className="w-5 h-5 text-amber-500" />
              </div>
              <div>
                <p className="text-xs text-gray-500 font-medium">Role</p>
                <p className="text-sm font-semibold text-gray-900">{data.jobPosition?.jobRole?.roleName || "—"}</p>
              </div>
            </div>
          </div>

          {/* Progress Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-start">
            {getSectionProgress().map((section) => {
              const isExpanded = expandedSection === section.id;
              const checklistSection = getChecklistSections().find(
                (s) => s.id === section.id
              );
              return (
                <div
                  key={section.id}
                  onClick={() => setExpandedSection(isExpanded ? null : section.id)}
                  className={`bg-white p-6 rounded-2xl border ${section.borderColor} hover:shadow-md transition-all text-left cursor-pointer`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className={`p-3 ${section.iconBg} rounded-xl`}>
                      <div className={section.iconColor}>{section.icon}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveTab(section.id);
                        }}
                        className="px-3 py-1.5 rounded-lg bg-slate-900 text-white text-xs font-medium hover:bg-slate-800 transition-colors"
                      >
                        Complete Checklist Items
                      </button>
                      {isExpanded ? (
                        <ChevronDown className="w-5 h-5 text-gray-300" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-gray-300" />
                      )}
                    </div>
                  </div>
                  <h3 className="text-base font-semibold text-gray-900 mb-1">{section.title}</h3>
                  <p className="text-sm text-gray-500 mb-4">{section.description}</p>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">Progress</span>
                      <span className="font-medium text-gray-900">{section.progress}%</span>
                    </div>
                    <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gray-900 rounded-full transition-all"
                        style={{ width: `${section.progress}%` }}
                      />
                    </div>
                  </div>

                  {/* Expanded Checklist */}
                  {isExpanded && checklistSection && (
                    <div className="mt-4 pt-4 border-t border-gray-100 space-y-2">
                      <p className="text-xs text-gray-500 font-medium">
                        {checklistSection.completedCount}/{checklistSection.totalCount} completed
                      </p>
                      {checklistSection.items.map((item) => (
                        <button
                          key={item.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            setActiveTab(section.id);
                          }}
                          className={`w-full flex items-center justify-between p-3 rounded-xl border text-left transition-colors ${
                            item.completed
                              ? "bg-green-50/50 border-green-100"
                              : "bg-white border-gray-100 hover:bg-gray-50"
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div className="flex-shrink-0">
                              {item.completed ? (
                                <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center">
                                  <CheckCircle2 className="w-3.5 h-3.5 text-white" />
                                </div>
                              ) : (
                                <div className="w-5 h-5 rounded-full border-2 border-gray-300 bg-white" />
                              )}
                            </div>
                            <div>
                              <p className={`text-sm font-medium ${item.completed ? "text-gray-500 line-through" : "text-gray-900"}`}>
                                {item.name}
                              </p>
                              {item.required && !item.completed && (
                                <Badge variant="outline" className="bg-red-50 text-red-600 border-red-200 text-xs mt-1">
                                  Required
                                </Badge>
                              )}
                            </div>
                          </div>
                          <ChevronRight className="w-4 h-4 text-gray-300 flex-shrink-0" />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {activeTab === "forms" && (
        <div className="space-y-6">
          {/* Forms Header */}
          <div>
            <h2 className="text-xl font-bold text-gray-900">Pre-Start Forms</h2>
            <p className="text-sm text-gray-500">
              Complete all required compliance forms before your start date
            </p>
          </div>

          {/* Forms Grid */}
          {(data.templateData?.forms?.length ?? 0) > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {data.templateData!.forms.map((templateForm) => {
                const name = templateForm.name || templateForm.title || "Form";
                const formId = String(templateForm.id);
                // Per-form status is computed server-side from the caller's
                // submissions (see GET /api/onboarding/my-portal).
                const status = templateForm.status || "Not Started";
                const progress = templateForm.progress ?? 0;
                const formPath =
                  formId === "new-starter-form"
                    ? "/my-onboarding/new-starter-form"
                    : `/my-onboarding/forms/${formId}`;
                return (
                  <div
                    key={templateForm.id}
                    className="bg-white rounded-2xl border border-gray-100 p-5 space-y-4"
                  >
                    {/* Form Header */}
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-gray-50 rounded-lg">
                          <FileText className="w-5 h-5 text-gray-500" />
                        </div>
                        <div>
                          <h3 className="text-sm font-semibold text-gray-900">{name}</h3>
                          {(templateForm.required ?? true) && (
                            <Badge variant="outline" className="bg-red-50 text-red-600 border-red-200 text-xs mt-1">
                              Required
                            </Badge>
                          )}
                        </div>
                      </div>
                      <Badge
                        variant="outline"
                        className={
                          status === "Completed"
                            ? "bg-green-50 text-green-700 border-green-200"
                            : status === "In Progress"
                            ? "bg-blue-50 text-blue-700 border-blue-200"
                            : "bg-gray-50 text-gray-600 border-gray-200"
                        }
                      >
                        {status}
                      </Badge>
                    </div>

                    {/* Progress Bar */}
                    <div className="space-y-1">
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-gray-500">Progress</span>
                        <span className="font-medium text-gray-900">{progress}%</span>
                      </div>
                      <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gray-900 rounded-full transition-all"
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                    </div>

                    {/* Action Button (hidden in HR preview: the form pages
                        load the caller's own submissions, not the hire's) */}
                    {!isPreviewMode &&
                      (status === "Completed" ? (
                        <button
                          onClick={() => navigate(formPath)}
                          className="w-full py-2.5 rounded-xl border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                          View Submission
                        </button>
                      ) : (
                        <button
                          onClick={() => navigate(formPath)}
                          className="w-full py-2.5 rounded-xl bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 transition-colors"
                        >
                          {status === "In Progress" ? "Continue Form" : "Start Form"}
                        </button>
                      ))}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-gray-100 p-8 text-center">
              <FileText className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <h3 className="text-sm font-medium text-gray-900 mb-1">No forms assigned</h3>
              <p className="text-xs text-gray-500">
                There are no pre-start forms assigned from your onboarding template.
              </p>
            </div>
          )}

          {/* Signed Contract — completed during Recruitment; read-only so
              the hire can always revisit and download it. Shown after the
              pre-start forms, in both the worker portal and HR preview. */}
          {onboardingData?.contract && onboardingData.contract.signed && (
            <div className="bg-white rounded-2xl border border-gray-100 p-5 space-y-4">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-emerald-50 rounded-lg">
                    <FileSignature className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900">Signed Contract</h3>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Signed{" "}
                      {formatContractDate(
                        onboardingData.contract.candidateSignedDate ||
                          onboardingData.contract.acceptedAt
                      )}
                      {onboardingData.contract.positionTitle
                        ? ` · ${onboardingData.contract.positionTitle}`
                        : ""}
                    </p>
                  </div>
                </div>
                <Badge
                  variant="outline"
                  className="bg-green-50 text-green-700 border-green-200 text-xs"
                >
                  Signed
                </Badge>
              </div>

              {/* Progress Bar — always complete; no action required */}
              <div className="space-y-1">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-500">Progress</span>
                  <span className="font-medium text-gray-900">100%</span>
                </div>
                <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full bg-gray-900 rounded-full" style={{ width: "100%" }} />
                </div>
              </div>

              {/* View / Download CTAs */}
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setViewingContract(true)}
                  className="flex items-center justify-center gap-2 py-2.5 rounded-xl border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <Eye className="w-4 h-4" />
                  View
                </button>
                <button
                  onClick={handleDownloadContract}
                  disabled={downloadingContract}
                  className="flex items-center justify-center gap-2 py-2.5 rounded-xl border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  {downloadingContract ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4" />
                  )}
                  Download
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === "policies" && (
        <div className="space-y-6">
          {/* Policies Header */}
          <div>
            <h2 className="text-xl font-bold text-gray-900">Required Reading</h2>
            <p className="text-sm text-gray-500">
              Read and acknowledge all required policies before your start date
            </p>
          </div>

          {/* Policies List */}
          <div className="space-y-3">
            {(data.templateData?.policies?.length ?? 0) > 0 ? (
              data.templateData!.policies.map((policy) => {
                const isAcknowledged = acknowledgedPolicies.has(String(policy.id));
                return (
                  <div
                    key={policy.id}
                    className={`flex items-center justify-between p-4 rounded-xl border ${
                      isAcknowledged
                        ? "bg-green-50/30 border-green-100"
                        : "bg-white border-gray-100"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="flex-shrink-0">
                        {isAcknowledged ? (
                          <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center">
                            <CheckCircle2 className="w-4 h-4 text-white" />
                          </div>
                        ) : (
                          <div className="w-6 h-6 rounded-full border-2 border-gray-300 bg-white flex items-center justify-center">
                            <ShieldCheck className="w-3.5 h-3.5 text-gray-400" />
                          </div>
                        )}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {policy.title}
                        </p>
                        {policy.description && (
                          <p className="text-xs text-gray-500 mt-0.5">
                            {policy.description}
                          </p>
                        )}
                        {isAcknowledged && (
                          <p className="text-xs text-green-600 mt-0.5">
                            Acknowledged
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {/* Policy content + acknowledgment are self-service
                          endpoints matched by the caller's email — hide them
                          in HR preview mode */}
                      {!isPreviewMode && (
                        <button
                          onClick={() => handleViewPolicy(policy.id)}
                          disabled={policyActionLoading}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-50"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                          </svg>
                          View
                        </button>
                      )}
                      {isAcknowledged ? (
                        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 text-xs font-medium">
                          Acknowledged
                        </Badge>
                      ) : isPreviewMode ? (
                        <Badge variant="outline" className="bg-gray-50 text-gray-600 border-gray-200 text-xs font-medium">
                          Pending
                        </Badge>
                      ) : (
                        <button
                          onClick={() => handleAcknowledgePolicy(policy)}
                          disabled={policyActionLoading}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 transition-colors disabled:opacity-50"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          Acknowledge
                        </button>
                      )}
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="bg-white rounded-xl border border-gray-100 p-8 text-center">
                <ShieldCheck className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                <h3 className="text-sm font-medium text-gray-900 mb-1">No policies assigned</h3>
                <p className="text-xs text-gray-500">
                  There are no policies to acknowledge from your onboarding template.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === "licences" && (
        <div className="space-y-6">
          {/* Licences Header */}
          <div>
            <h2 className="text-xl font-bold text-gray-900">Licences & Qualifications</h2>
            <p className="text-sm text-gray-500">
              Upload the licences and qualifications required for your role. Mandatory items must be submitted before your start date.
            </p>
          </div>

          {/* Stats Cards */}
          <div className="bg-white rounded-2xl border border-gray-100 p-6">
            <div className="flex items-center gap-8">
              <div className="text-center">
                <p className="text-3xl font-bold text-gray-900">{uploadedCount}</p>
                <p className="text-sm text-gray-500">Uploaded</p>
              </div>
              <div className="w-px h-12 bg-gray-200" />
              <div className="text-center">
                <p className="text-3xl font-bold text-red-600">{mandatoryMissingCount}</p>
                <p className="text-sm text-gray-500">Mandatory Missing</p>
              </div>
              <div className="w-px h-12 bg-gray-200" />
              <div className="text-center">
                <p className="text-3xl font-bold text-gray-900">{optionalCount}</p>
                <p className="text-sm text-gray-500">Optional</p>
              </div>
            </div>
          </div>

          {/* Hidden file input for licence uploads */}
          <input
            ref={licenceFileInputRef}
            type="file"
            className="hidden"
            accept="image/*,.pdf"
            onChange={handleLicenceFileSelected}
          />

          {/* Licences List */}
          {licences.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-100 p-8 text-center">
              <Award className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <h3 className="text-sm font-medium text-gray-900 mb-1">No licences required</h3>
              <p className="text-xs text-gray-500">
                There are no licences or qualifications assigned from your onboarding template.
              </p>
            </div>
          ) : (
          <div className="space-y-3">
            {licences.map((licence) => (
              <div
                key={licence.id}
                className={`rounded-2xl border p-5 ${
                  licence.uploaded
                    ? "bg-green-50/30 border-green-100"
                    : "bg-white border-gray-100"
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-gray-50 rounded-lg">
                      <FileText className="w-5 h-5 text-gray-400" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="text-sm font-semibold text-gray-900">{licence.name}</h3>
                        {licence.mandatory ? (
                          <Badge variant="outline" className="bg-red-50 text-red-600 border-red-200 text-xs">
                            Mandatory
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="bg-gray-50 text-gray-600 border-gray-200 text-xs">
                            Optional
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Upload evidence. If the license, ticket or qualification has an expiry, please enter an expiry date, otherwise leave blank.
                      </p>
                    </div>
                  </div>
                  {!licence.uploaded && !isPreviewMode && (
                    <button
                      onClick={() => handleUploadClick(licence.id)}
                      disabled={licenceActionLoading}
                      className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 transition-colors disabled:opacity-50"
                    >
                      {licenceActionLoading && uploadTargetLicence === licence.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                      )}
                      {licenceActionLoading && uploadTargetLicence === licence.id ? "Uploading..." : "Upload"}
                    </button>
                  )}
                </div>

                {/* Uploaded File Details */}
                {licence.uploaded && (
                  <div className="mt-4 space-y-3">
                    <div className="flex items-center gap-2 text-sm text-gray-700">
                      <FileText className="w-4 h-4 text-green-500" />
                      {licence.attachmentId ? (
                        <a
                          href={`${API_BASE}/api/qualifications/attachments/${licence.attachmentId}/download?mode=view`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline"
                        >
                          {licence.fileName}
                        </a>
                      ) : (
                        <span>{licence.fileName}</span>
                      )}
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <label className="text-sm text-gray-500">Issue date: *</label>
                        <Input
                          type="date"
                          value={licence.issueDate || ""}
                          onChange={(e) => handleLicenceDateChange(licence.id, "issueDate", e.target.value)}
                          disabled={isPreviewMode}
                          className={`h-8 w-40 text-sm rounded-lg ${!licence.issueDate ? "border-red-300" : ""}`}
                        />
                        {!licence.issueDate && (
                          <span className="text-xs text-red-500">Required</span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <label className="text-sm text-gray-500">Expiry date:</label>
                        <Input
                          type="date"
                          value={licence.expiryDate || ""}
                          onChange={(e) => handleLicenceDateChange(licence.id, "expiryDate", e.target.value)}
                          disabled={isPreviewMode}
                          className="h-8 w-40 text-sm rounded-lg"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
          )}
        </div>
      )}

      {activeTab === "learning" && (
        <div className="space-y-6">
          {/* Learning Header */}
          <div>
            <h2 className="text-xl font-bold text-gray-900">Learning</h2>
            <p className="text-sm text-gray-500">
              These are your pre-start training courses — work through them before your first day. Each one opens in a new tab.
            </p>
          </div>

          {/* Stats Cards */}
          <div className="bg-white rounded-2xl border border-gray-100 p-6">
            <div className="flex items-center gap-8">
              <div className="text-center">
                <p className="text-3xl font-bold text-gray-900">{learningCompletedCount}</p>
                <p className="text-sm text-gray-500">Completed</p>
              </div>
              <div className="w-px h-12 bg-gray-200" />
              <div className="text-center">
                <p className="text-3xl font-bold text-indigo-600">{learningInProgressCount}</p>
                <p className="text-sm text-gray-500">In Progress</p>
              </div>
              <div className="w-px h-12 bg-gray-200" />
              <div className="text-center">
                <p className="text-3xl font-bold text-gray-900">{learningNotStartedCount}</p>
                <p className="text-sm text-gray-500">Not Started</p>
              </div>
            </div>
          </div>

          {/* Course List */}
          {learningItems.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-100 p-8 text-center">
              <GraduationCap className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <h3 className="text-sm font-medium text-gray-900 mb-1">No courses required</h3>
              <p className="text-xs text-gray-500">
                There is no pre-start training assigned from your onboarding template.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {learningItems.map((course) => (
                <div
                  key={course.id}
                  className={`flex flex-col gap-4 rounded-2xl border p-5 ${
                    course.status === "Completed"
                      ? "bg-green-50/30 border-green-100"
                      : "bg-white border-gray-100"
                  }`}
                >
                  <div className="flex items-start gap-4">
                    {/* Thumbnail — the API signs it; fall back to an icon block */}
                    <div className="flex h-16 w-24 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-indigo-50">
                      {course.thumbnailUrl ? (
                        <img
                          src={course.thumbnailUrl}
                          alt=""
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <BookOpen className="w-6 h-6 text-indigo-400" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-2">
                        <h3 className="text-sm font-semibold text-gray-900">{course.title}</h3>
                        <Badge
                          variant="outline"
                          className={
                            course.status === "Completed"
                              ? "bg-green-50 text-green-700 border-green-200"
                              : course.status === "In Progress"
                              ? "bg-blue-50 text-blue-700 border-blue-200"
                              : "bg-gray-50 text-gray-600 border-gray-200"
                          }
                        >
                          {course.status === "Completed"
                            ? "Completed"
                            : course.status === "In Progress"
                            ? "In progress"
                            : "Not started"}
                        </Badge>
                      </div>
                      {course.description && (
                        <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                          {course.description}
                        </p>
                      )}
                      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
                        <Badge
                          variant="outline"
                          className="bg-indigo-50 text-indigo-700 border-indigo-200 text-xs"
                        >
                          {courseContentTypeLabel(course.contentType)}
                        </Badge>
                        <span className="inline-flex items-center gap-1">
                          <Clock className="w-3.5 h-3.5" />
                          {durationLabel(course.estimatedDurationMinutes)}
                        </span>
                        {course.issuesCertificate && (
                          <span className="inline-flex items-center gap-1">
                            <Award className="w-3.5 h-3.5" />
                            Certificate issued on completion
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* CTA — a real anchor so it opens in a new tab on mobile; HR
                      preview renders it inert (the preview is read-only). */}
                  {isPreviewMode ? (
                    <button
                      type="button"
                      disabled
                      className="w-full py-2.5 rounded-xl bg-gray-100 text-sm font-medium text-gray-400 cursor-not-allowed"
                    >
                      {course.status === "Completed"
                        ? "Completed"
                        : course.status === "In Progress"
                        ? "Continue"
                        : "Start course"}
                    </button>
                  ) : (
                    <a
                      href={`/my-onboarding/courses/${course.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex w-full items-center justify-center gap-2 py-2.5 rounded-xl bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 transition-colors"
                    >
                      <PlayCircle className="w-4 h-4" />
                      {course.status === "Completed"
                        ? "Completed"
                        : course.status === "In Progress"
                        ? "Continue"
                        : "Start course"}
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === "support" && (
        <div className="space-y-6">
          {/* Support Network Header */}
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Your Support Network</h2>
            <p className="text-sm text-gray-500 mt-1">The people who will make your onboarding experience amazing</p>
          </div>

          {/* Support Contacts Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {(() => {
              const supportNetwork = data.templateData?.supportNetwork;
              if (!supportNetwork || supportNetwork.length === 0) {
                return (
                  <div className="col-span-full bg-white rounded-xl border border-gray-100 p-8 text-center">
                    <Users className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                    <h3 className="text-sm font-medium text-gray-900 mb-1">No support network assigned</h3>
                    <p className="text-xs text-gray-500">
                      Your support network will be assigned from your onboarding template.
                    </p>
                  </div>
                );
              }
              return supportNetwork.map((contact) => {
                const user = contact.user;
                const initials = user?.name
                  ?.split(" ")
                  .map((n) => n[0])
                  .join("")
                  .toUpperCase() || contact.label.split(" ").map((n) => n[0]).join("").toUpperCase();

                const colorMap: Record<number, { bg: string; text: string; lightBg: string }> = {
                  0: { bg: "bg-blue-500", text: "text-blue-600", lightBg: "bg-blue-50" },
                  1: { bg: "bg-purple-500", text: "text-purple-600", lightBg: "bg-purple-50" },
                  2: { bg: "bg-green-500", text: "text-green-600", lightBg: "bg-green-50" },
                  3: { bg: "bg-orange-500", text: "text-orange-600", lightBg: "bg-orange-50" },
                  4: { bg: "bg-gray-500", text: "text-gray-600", lightBg: "bg-gray-50" },
                };
                const colorIndex = Math.abs(contact.id.split("").reduce((a, b) => a + b.charCodeAt(0), 0)) % 5;
                const colors = colorMap[colorIndex];

                return (
                  <div
                    key={contact.id}
                    className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-md transition-shadow"
                  >
                    {/* Colored top border */}
                    <div className={`h-1.5 ${colors.bg}`} />

                    <div className="p-5">
                      {/* Name + Avatar row */}
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full ${colors.bg} flex items-center justify-center text-white font-semibold text-sm shrink-0`}>
                          {initials}
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-gray-900 truncate">
                            {user?.name || contact.label}
                          </p>
                          <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-gray-100 text-gray-700 mt-0.5">
                            {contact.label}
                          </span>
                        </div>
                      </div>

                      {/* Description */}
                      <p className="text-sm text-gray-500 mt-3 leading-relaxed">
                        {getSupportRoleDescription(contact.id)}
                      </p>

                      {/* Contact info */}
                      {user && (
                        <div className="mt-4 space-y-1.5">
                          <div className="flex items-center gap-2 text-sm">
                            <Mail className="w-4 h-4 text-gray-400 shrink-0" />
                            <a href={`mailto:${user.email}`} className={`${colors.text} hover:underline truncate`}>
                              {user.email}
                            </a>
                          </div>
                          {user.mobile && (
                            <div className="flex items-center gap-2 text-sm">
                              <Phone className="w-4 h-4 text-gray-400 shrink-0" />
                              <a href={`tel:${user.mobile.replace(/\s/g, "")}`} className="text-gray-600 hover:text-gray-900">
                                {user.mobile}
                              </a>
                            </div>
                          )}
                        </div>
                      )}
                      {!user && (
                        <div className="mt-4 p-3 bg-gray-50 rounded-lg text-xs text-gray-500">
                          Contact details will be provided soon.
                        </div>
                      )}
                    </div>
                  </div>
                );
              });
            })()}
          </div>
        </div>
      )}

      {activeTab === "training" && (
        <div className="space-y-6">
          {/* Training Plan Header */}
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {data.trainingPlanData?.name || "Training Plan"}
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                {data.trainingPlanData?.description || "Complete all assigned procedures for your role"}
              </p>
            </div>
            {data.trainingPlanData && (
              <Badge
                variant="outline"
                className={
                  data.trainingPlanData.status === "Completed"
                    ? "bg-green-50 text-green-700 border-green-200"
                    : data.trainingPlanData.status === "In Progress"
                    ? "bg-blue-50 text-blue-700 border-blue-200"
                    : "bg-gray-50 text-gray-600 border-gray-200"
                }
              >
                {data.trainingPlanData.status}
              </Badge>
            )}
          </div>

          {/* Procedures List */}
          {(data.trainingPlanData?.procedures?.length ?? 0) > 0 ? (
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="px-5 py-3 bg-gray-50/70 border-b border-gray-100">
                <span className="text-xs font-semibold text-gray-500 tracking-wide uppercase">
                  Procedures ({data.trainingPlanData!.procedures.length})
                </span>
              </div>
              <div className="divide-y divide-gray-50">
                {data.trainingPlanData!.procedures.map((procedure) => (
                  <div
                    key={procedure.id}
                    onClick={() => {
                      setSelectedProcedure(procedure);
                      setShowProcedureModal(true);
                    }}
                    className="px-5 py-4 flex items-center justify-between hover:bg-gray-50/50 transition-colors cursor-pointer"
                  >
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-slate-100 rounded-lg">
                        <BookOpen className="w-4 h-4 text-slate-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{procedure.name}</p>
                        {procedure.description && (
                          <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{procedure.description}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="bg-gray-50 text-gray-600 border-gray-200 text-xs">
                        {procedure.cadence}
                      </Badge>
                      <Eye className="w-4 h-4 text-gray-400" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-gray-100 p-8 text-center">
              <GraduationCap className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <h3 className="text-sm font-medium text-gray-900 mb-1">No training plan assigned</h3>
              <p className="text-xs text-gray-500">
                A training plan will be assigned from your onboarding setup.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Procedure Detail Modal */}
      <Dialog open={showProcedureModal} onOpenChange={setShowProcedureModal}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-slate-600" />
              {selectedProcedure?.name}
            </DialogTitle>
          </DialogHeader>

          {selectedProcedure && (
            <div className="space-y-6 pt-2">
              {/* Meta info */}
              <div className="flex items-center gap-3">
                <Badge variant="outline" className="bg-gray-50 text-gray-600 border-gray-200 text-xs">
                  {selectedProcedure.cadence}
                </Badge>
                <span className="text-xs text-gray-400">
                  Version {selectedProcedure.version}
                </span>
              </div>

              {/* Description */}
              {selectedProcedure.description && (
                <div className="space-y-2">
                  <h4 className="text-sm font-semibold text-gray-900">Description</h4>
                  <p className="text-sm text-gray-600 leading-relaxed">{selectedProcedure.description}</p>
                </div>
              )}

              {/* Why Important */}
              {selectedProcedure.whyImportant && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 text-amber-500" />
                    <h4 className="text-sm font-semibold text-gray-900">Why This Matters</h4>
                  </div>
                  <div className="bg-amber-50 rounded-lg p-4 border border-amber-100">
                    <p className="text-sm text-amber-800 leading-relaxed">{selectedProcedure.whyImportant}</p>
                  </div>
                </div>
              )}

              {/* How to Complete */}
              {(() => {
                const steps = selectedProcedure.steps;
                const howToComplete = selectedProcedure.howToComplete;
                if (!(steps && steps.length > 0) && !howToComplete) return null;
                return (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                      <h4 className="text-sm font-semibold text-gray-900">How to Complete</h4>
                    </div>
                    {steps && steps.length > 0 ? (
                      // Same numbered-step layout as the procedure detail page
                      <div className="space-y-4">
                        {steps.map((step) => (
                          <div key={step.id} className="flex items-start gap-3">
                            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-600 text-white text-xs flex items-center justify-center font-medium mt-0.5">
                              {step.number}
                            </span>
                            <div className="flex-1">
                              <h4 className="font-medium text-sm">
                                {step.title || `Step ${step.number}`}
                              </h4>
                              <div
                                className="text-sm text-gray-600 mt-1 prose prose-sm max-w-none"
                                dangerouslySetInnerHTML={{ __html: step.content || "<p></p>" }}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      // Legacy procedures without procedure_steps rows
                      howToComplete && (
                        <div className="bg-green-50 rounded-lg p-4 border border-green-100">
                          <div
                            className="prose prose-sm max-w-none text-green-800"
                            dangerouslySetInnerHTML={{
                              __html: howToComplete.includes("<")
                                ? howToComplete
                                : howToComplete.replace(/\n/g, "<br />"),
                            }}
                          />
                        </div>
                      )
                    )}
                  </div>
                );
              })()}

              {/* Video Embed */}
              {selectedProcedure.videoEmbed && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <PlayCircle className="w-4 h-4 text-blue-500" />
                    <h4 className="text-sm font-semibold text-gray-900">Training Video</h4>
                  </div>
                  <div className="aspect-video bg-gray-100 rounded-lg flex items-center justify-center">
                    <p className="text-sm text-gray-500">Video content would be embedded here</p>
                  </div>
                </div>
              )}

              {/* Close button */}
              <div className="pt-4 border-t border-gray-100">
                <button
                  onClick={() => setShowProcedureModal(false)}
                  className="w-full py-2.5 rounded-lg bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Policy View Modal */}
      <Dialog open={showPolicyModal} onOpenChange={setShowPolicyModal}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-slate-600" />
              {selectedPolicy?.title}
            </DialogTitle>
          </DialogHeader>

          {selectedPolicy && (
            <div className="space-y-6 pt-2">
              {selectedPolicy.description && (
                <p className="text-sm text-gray-500">{selectedPolicy.description}</p>
              )}

              {selectedPolicy.content && (
                <div
                  className="prose max-w-none text-sm"
                  dangerouslySetInnerHTML={{
                    __html: selectedPolicy.content.includes("<")
                      ? selectedPolicy.content
                      : selectedPolicy.content.replace(/\n/g, "<br />"),
                  }}
                />
              )}

              {selectedPolicy.fileUrl && (
                <a
                  href={selectedPolicy.fileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-sm text-blue-600 hover:underline"
                >
                  <FileText className="w-4 h-4" />
                  Download attachment
                </a>
              )}

              <div className="pt-4 border-t border-gray-100">
                <button
                  onClick={() => setShowPolicyModal(false)}
                  className="w-full py-2.5 rounded-lg bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Contract View Modal */}
      <Dialog open={viewingContract} onOpenChange={setViewingContract}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <FileSignature className="w-5 h-5 text-emerald-600" />
              Signed Contract
            </DialogTitle>
          </DialogHeader>
          {onboardingData?.contract && (
            <div className="pt-2">
              <SignedContractDocument contract={onboardingData.contract} />
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Off-screen host for the contract PDF capture — mounted only while a
          download runs, so the capture never appears on screen. Mirrors the
          recruitment Offer letter PDF: the captured node is a plain block
          with an explicit pixel width inside a fixed off-screen wrapper. */}
      {onboardingData?.contract && downloadingContract && (
        <div
          style={{ position: "fixed", left: "-10000px", top: 0, zIndex: -1, width: "794px" }}
          aria-hidden="true"
        >
          <style>{`
            .contract-letter-pdf p { margin: 0; padding: 0; }
            .contract-letter-pdf p + p { margin-top: 0.5em; }
            .contract-letter-pdf br { display: block; content: ""; margin-top: 0.5em; }
            .contract-letter-pdf ul, .contract-letter-pdf ol { margin: 0.5em 0; padding-left: 1.5em; }
            .contract-letter-pdf h1, .contract-letter-pdf h2, .contract-letter-pdf h3,
            .contract-letter-pdf h4, .contract-letter-pdf h5, .contract-letter-pdf h6 { margin: 0.5em 0; font-weight: 600; }
          `}</style>
          <div ref={contractRef}>
            <SignedContractDocument contract={onboardingData.contract} bare />
          </div>
        </div>
      )}

      {/* Onboarding Completion Celebration */}
      {showCelebration && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/95 p-4 animate-in fade-in duration-500">
          <div className="bg-slate-800 text-white p-8 rounded-2xl shadow-2xl border border-slate-700 text-center max-w-md w-full">
            <div className="w-16 h-16 bg-emerald-500/20 text-emerald-400 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
              <CheckCircle2 className="w-8 h-8" />
            </div>
            <h2 className="text-2xl font-bold mb-2">You're all set! 🎉</h2>
            <p className="text-slate-400 mb-4">
              You've successfully completed your onboarding with us. Welcome to
              the team.
            </p>
            <p className="text-slate-400 mb-6">
              You're all set! Now that you've completed your onboarding, you can
              close this window—or feel free to explore your Support Network and
              Training Plan anytime to prepare for your first day.
              Congratulations and welcome!
            </p>
            <button
              onClick={() => {
                if (celebrationKey) {
                  localStorage.setItem(celebrationKey, "true");
                }
                setShowCelebration(false);
                setActiveTab("support");
              }}
              className="bg-green-600 hover:bg-green-500 text-white font-semibold py-3 px-6 rounded-xl shadow-lg transition-all duration-200 transform hover:scale-105 active:scale-95"
            >
              Let's Go!
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyOnboardingPortal;
